import { useCallback, useEffect } from "react";
import { Navigate, useParams } from "react-router";

import { FilterButton, MasterDetailLayout } from "common/components";
import { getPageId } from "common/utilities";
import { useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { getSiteLanguage } from "domains/language/selectors";
import { activateModal } from "domains/modal/actions";
import { getTopicsFilterSelectedIds } from "domains/monitor/filter/selectors";
import { fetchLatestKnowledge } from "domains/monitor/latestKnowledge/actions";
import {
  getLatestKnowledgeHasNextPage,
  getLatestKnowledgeIsPending,
  getLatestKnowledgeItems,
  getLatestKnowledgeOffset,
} from "domains/monitor/latestKnowledge/selectors";
import { KnowledgeDetail } from "domains/publication/components/KnowledgeDetail";
import { KnowledgeList } from "domains/publication/components/KnowledgeList";
import { createKnowledgesSelector, getKnowledgeById } from "entities/knowledge/selectors";
import { getCanAccessMonitor } from "entities/permissions/selectors";
import { createTopicsSelector, getFollowedTopicsIds } from "entities/topics/selectors";

export const LatestKnowledgePage = () => {
  const dispatch = useDispatch();
  const followingTopics = useMemoisedSelector(getFollowedTopicsIds, createTopicsSelector);
  const items = useMemoisedSelector(getLatestKnowledgeItems, createKnowledgesSelector);
  const { id: paramId } = useParams();
  const hasAccessToMonitor = useSelector(getCanAccessMonitor);
  const hasNextPage = useSelector(getLatestKnowledgeHasNextPage);
  const isPending = useSelector(getLatestKnowledgeIsPending);
  const offset = useSelector(getLatestKnowledgeOffset);
  const language = useSelector(getSiteLanguage);
  const selectedTopics = useSelector(getTopicsFilterSelectedIds);
  const track = useTrack();
  const translate = useTranslate();

  const id = getPageId(paramId, items);

  const knowledge = useSelector(getKnowledgeById(id));

  useEffect(() => {
    if (items.length === 0) {
      dispatch(fetchLatestKnowledge({ offset: 0, filters: { topic: selectedTopics } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, items.length, selectedTopics.length]);

  useEffect(() => {
    if (items.length !== 0) {
      dispatch(fetchLatestKnowledge({ offset: 0, filters: { topic: selectedTopics } }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedTopics.length, hasAccessToMonitor, language]);

  const knowledgeTitle = knowledge?.title;

  useEffect(() => {
    if (!paramId || !knowledgeTitle) {
      return;
    }
    track({
      entityId: paramId,
      entityName: knowledgeTitle,
      entityType: "Knowledge",
      name: "Browse page",
      page: "Monitor",
      subpage: "Knowledge detail",
    });
  }, [knowledgeTitle, paramId, track]);

  useEffect(() => {
    if (paramId) {
      return;
    }
    track({ name: "Browse page", page: "Monitor", subpage: "Latest Knowledge" });
  }, [paramId, track]);

  const loadNextPage = useCallback(() => {
    dispatch(
      fetchLatestKnowledge({
        offset: offset + 1,
        filters: { topic: selectedTopics },
      }),
    );
  }, [dispatch, offset, selectedTopics]);

  if (!hasAccessToMonitor && !id) {
    return <Navigate to="/monitor" replace />;
  }

  const getToLink = (knowledgeId: string) => `/monitor/latest-knowledge/${knowledgeId}`;

  const list = (
    <KnowledgeList
      items={items}
      isPending={isPending}
      hasNextPage={hasNextPage}
      loadNextPage={loadNextPage}
      getToLink={getToLink}
      selectedId={id}
      lockTiles={!hasAccessToMonitor}
      trackPage="Knowledge detail"
      type="medium"
    />
  );

  const shareLink = knowledge?.id ? `${import.meta.env.VITE_WEBSITE_URL}${getToLink(knowledge.id)}` : undefined;

  const openFilterModal = () =>
    dispatch(
      activateModal({
        id: "MonitorFilter",
      }),
    );

  const filterButton =
    followingTopics.length !== 0 && hasAccessToMonitor ? (
      <FilterButton count={selectedTopics.length} onClick={openFilterModal} />
    ) : undefined;

  return (
    <MasterDetailLayout
      listTitle={translate("app.monitor.latest_knowledge")}
      listComponent={list}
      listBackNavigation={{
        label: translate("app.monitor.title"),
        path: "/monitor",
      }}
      listHeaderRightElement={filterButton}
      detailBackNavigation={{
        label: translate("app.monitor.latest_knowledge"),
        path: "/monitor/latest-knowledge",
      }}
      mainView={paramId ? "right" : "left"}
    >
      <KnowledgeDetail
        id={id}
        knowledge={knowledge}
        shareLink={shareLink}
        topicBack={{ term: "app.monitor.latest_knowledge" }}
      />
    </MasterDetailLayout>
  );
};
